import { useContext, useEffect, useMemo, useState } from 'react'
import { CookiesProvider } from 'react-cookie'
import {
  BrowserRouter as Router,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import { Provider as UrqlProvider } from 'urql'
import { Provider as StoreProvider, useAtomValue } from 'jotai'
import { getClient } from './lib/client'
import { ComplaintPage } from './pages/Complaint'
import { ContactPage } from './pages/Contact'
import { LoginPage } from './pages/Login'
import { LoginRecoverPage } from './pages/LoginRecover'
import { OrdersPage } from './pages/Orders'
import { ProfilesPage } from './pages/Profiles'
import { RegisterPage } from './pages/Register'
import { RegisterFinishPage } from './pages/RegisterFinish'
import { ResetPasswordFinishPage } from './pages/ResetPasswordFinish'
import { ResetPasswordInitPage } from './pages/ResetPasswordInit'
import { AuthContext, useAuth } from './provider/auth'
import { BasketContext, useBasket } from './provider/basket'
import { I18nContext, useI18n } from './provider/i18n'
import { AddressSelectorPage } from './pages/AddressSelector'
import { CatalogPage } from './pages/Catalog'
import { CatalogIndexPage } from './pages/catalog/Index'
import { CatalogTagPage } from './pages/catalog/Tag'
import { OrdersIndexPage } from './pages/orders/Index'
import { CatalogTestPage } from './pages/catalog/Test'
import { BasketPage, NewBasket } from './pages/basket'
import { Navigate, useNavigate, useParams } from 'react-router'
import { NotFoundPage } from './pages/NotFound'
import { PingDocument } from './generated/urql.anonymous'
import { OrdersOrderPage } from './pages/orders/Order'
import { ProtectedRoute, ShippingTestsRoute } from './components/ProtectedRoute'
import { CatalogConsultationPage } from './pages/catalog/Consultation'
import { CatalogTrainingPage } from './pages/catalog/Training'
import { store } from './store'
import RootSubscriptions from './components/RootSubscriptions'
import MessagesContainer from './components/Messages'
import {
  Layout,
  Tests,
  Consultations,
  ShippingTests,
  ShippingTestsByTag,
  Trainings,
} from './pages/new-catalog'
import {
  isFfErrorAtom,
  isFfLoadingAtom,
  isNewLayoutEnabledAtom,
  isShowShippingTestsEnabledAtom,
} from './store/featureFlags.atoms'
import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from './lib/colors'
import { CatalogSearchPage } from './pages/catalog/Search'
import SearchTests from './pages/new-catalog/search/SearchTests'
import SearchShippingTests from './pages/new-catalog/search/SearchShippingTests'
import SearchConsultations from './pages/new-catalog/search/SearchConsultations'
import SearchTrainings from './pages/new-catalog/search/SearchTrainings'
import ShippingTest from './pages/new-catalog/ShippingTest'

const RedirectOrder = () => {
  const params = useParams<{ id: string }>()
  return <Navigate to={`/orders/${params.id}`} />
}

const RedirectLegacyTagToTestsCatalog = () => {
  const { tagId } = useParams<{ tagId: string }>()
  return (
    <Navigate
      to={`/tests/tag/${encodeURIComponent(tagId || '')}`}
      replace
    />
  )
}

const RedirectLegacySearchToTestsSearch = () => {
  const location = useLocation()
  return <Navigate to={`/tests/search${location.search}`} replace />
}

const EnsureClientLocation = () => {
  const basket = useContext(BasketContext)
  const location = useLocation()
  const navigate = useNavigate()

  if (
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/resetpassword')
  ) {
    return <Outlet />
  }

  if (!basket.address?.city) {
    return <Navigate to={`/address-selector`} />
  }

  return <Outlet />
}

function AnimatedRoutes() {
  const [ffInited, setFfInited] = useState(false)
  const location = useLocation()
  const i18n = useI18n()
  const isNewLayoutEnabled = useAtomValue(isNewLayoutEnabledAtom)
  const isShowShippingTestsEnabled = useAtomValue(isShowShippingTestsEnabledAtom)
  const isFfLoading = useAtomValue(isFfLoadingAtom)
  const isFfError = useAtomValue(isFfErrorAtom)

  useEffect(() => {
    if (!isFfLoading && !ffInited) {
      setFfInited(true)
    }
  }, [ffInited, isFfLoading])

  if (isFfError) {
    return (
      <div className="flex flex-col justify-center items-center p-8 gap-4">
        <p className="text-lg">{i18n.t('app.error.generic')}</p>
        <button className="hl-button" onClick={() => window.location.reload()}>
          {i18n.t('common.refresh')}
        </button>
      </div>
    )
  }

  if (!ffInited) {
    return (
      <TailSpin
        color={Blue300}
        height={32}
        width={32}
        wrapperClass="flex justify-center p-8"
      />
    )
  }

  return (
    <Routes location={location}>
      <Route element={<EnsureClientLocation />}>
        {isNewLayoutEnabled ? (
          <Route path="/" element={<Layout />}>
            <Route
              index
              element={<Navigate to={isShowShippingTestsEnabled ? '/shipping-tests' : '/tests'} replace />}
            />
            <Route path="tests" element={<Tests />} />
            <Route path="/tests/tag/:tagId" element={<CatalogTagPage />} />
            <Route path="/tests/search" element={<SearchTests />} />
            <Route
              path="/search"
              element={<RedirectLegacySearchToTestsSearch />}
            />
            <Route element={<ShippingTestsRoute />}>
              <Route path="/shipping-tests" element={<ShippingTests />} />
              <Route
                path="/shipping-tests/tag/:tagId"
                element={<ShippingTestsByTag />}
              />
              <Route
                path="/shipping-tests/search"
                element={<SearchShippingTests />}
              />
              <Route path="/shipping-tests/:id" element={<ShippingTest />} />
            </Route>
            <Route path="/consultation" element={<Consultations />} />
            <Route
              path="/consultation/search"
              element={<SearchConsultations />}
            />
            <Route
              path="/consultation/:id"
              element={<CatalogConsultationPage />}
            />
            <Route path="/training" element={<Trainings />} />
            <Route path="/training/search" element={<SearchTrainings />} />
            <Route path="/training/:id" element={<CatalogTrainingPage />} />
            <Route
              path="/tag/:tagId"
              element={<RedirectLegacyTagToTestsCatalog />}
            />
            <Route path="/test/:id" element={<CatalogTestPage />} />
          </Route>
        ) : (
          <Route path="/" element={<CatalogPage />}>
            <Route index element={<CatalogIndexPage />} />
            <Route path="/search" element={<CatalogSearchPage />} />
            <Route path="/tag/:tagId" element={<CatalogTagPage />} />
            <Route path="/test/:id" element={<CatalogTestPage />} />
            <Route
              path="/consultation/:id"
              element={<CatalogConsultationPage />}
            />
            <Route path="/training/:id" element={<CatalogTrainingPage />} />
          </Route>
        )}
        <Route path="/basket" element={<BasketPage />} />
        <Route path="/newbasket" element={<NewBasket />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/recover" element={<LoginRecoverPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/finish" element={<RegisterFinishPage />} />
        <Route path="/resetpassword" element={<ResetPasswordInitPage />} />
        <Route
          path="/resetpassword/finish"
          element={<ResetPasswordFinishPage />}
        />
        <Route element={<ProtectedRoute />}>
          <Route path="/profiles" element={<ProfilesPage />} />
          <Route path="/profiles/:id" element={<ProfilesPage />} />
          <Route path="/orders" element={<OrdersPage />}>
            <Route index element={<OrdersIndexPage />} />
            <Route path="/orders/:id" element={<OrdersOrderPage />} />
          </Route>
        </Route>
        <Route path="/compliant" element={<ComplaintPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="/address-selector" element={<AddressSelectorPage />} />
      <Route path="/order/:id" element={<RedirectOrder />} />
    </Routes>
  )
}

function App() {
  const auth = useAuth()
  const basket = useBasket()
  const i18n = useI18n()
  const client = useMemo(() => getClient(auth, i18n), [auth, i18n])

  useEffect(() => {
    const interval = setInterval(() => {
      if (auth.isLogged) {
        client.mutation(PingDocument, {}).toPromise().catch(console.error)
      }
    }, 60000)
    return () => clearInterval(interval)
  })

  return (
    <CookiesProvider>
      <I18nContext.Provider value={i18n}>
        <AuthContext.Provider value={auth}>
          <BasketContext.Provider value={basket}>
            <UrqlProvider value={client}>
              <StoreProvider store={store}>
                <Router>
                  <RootSubscriptions />
                  <MessagesContainer />
                  <AnimatedRoutes />
                </Router>
              </StoreProvider>
            </UrqlProvider>
          </BasketContext.Provider>
        </AuthContext.Provider>
      </I18nContext.Provider>
    </CookiesProvider>
  )
}

export default App
